import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { lessonsAPI, completionsAPI, questsAPI, askOrbAPI, analyticsAPI, skillProgressAPI, journeyAPI, billingAPI } from '../api';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, CheckCircle, ExternalLink, Sparkles, MessageCircle, Send, X, Loader2, Star, Trophy, ChevronRight, Compass, Map } from 'lucide-react';
import { AvatarPreview } from '../components/AvatarBuilder';
import ActivityLimitModal from '../components/ActivityLimitModal';
import LessonPhaseIndicator, { getPhaseForBlock } from '../components/LessonPhaseIndicator';
import AretHeraVoice from '../components/AretHeraVoice';
import { 
  LearningIntelligenceProvider, 
  useLearningIntelligence, 
  ExtraExampleModal, 
  HintLayer, 
  FastTrackIndicator,
  IntelligenceStats 
} from '../components/LearningIntelligence';
import {
  MatchingPairs,
  DragDropOrder,
  SortingActivity,
  FillInBlank,
  QuickRecall,
  LogicPuzzle,
  CreativeWriting,
  DrawingActivity,
  MusicRhythm,
  ReflectionActivity,
  TrueFalse,
  StoryNarration
} from '../components/LessonInteractions';

const LessonPlayerPage = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [currentBlockIndex, setCurrentBlockIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  
  // Track block completion for interactive blocks
  const [blockCompleted, setBlockCompleted] = useState(false);
  
  // Track lesson progress for analytics
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  
  // Session timer for analytics
  const sessionStartTime = useRef(null);
  
  // Quest completion state
  const [questReward, setQuestReward] = useState(null);
  
  // Next lesson recommendation state
  const [nextLessonData, setNextLessonData] = useState(null);
  const [showSparkAnimation, setShowSparkAnimation] = useState(false);
  
  // Journey progress state
  const [journeyProgress, setJourneyProgress] = useState(null);
  
  // Activity limit state
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitData, setLimitData] = useState({ activitiesCompleted: 0, dailyLimit: 5 });
  
  // Ask the Orb state
  const [showOrbChat, setShowOrbChat] = useState(false);
  const [orbQuestion, setOrbQuestion] = useState('');
  const [orbLoading, setOrbLoading] = useState(false);
  const [orbConversation, setOrbConversation] = useState([]);

  const selectedChild = JSON.parse(localStorage.getItem('selectedChild') || 'null');
  const activeQuestId = localStorage.getItem('activeQuestId');

  // Reset all state when lessonId changes (critical for Next Lesson navigation)
  useEffect(() => {
    // Reset all state for new lesson
    setLesson(null);
    setCurrentBlockIndex(0);
    setUserAnswer('');
    setShowFeedback(false);
    setIsCorrect(false);
    setLoading(true);
    setCompleted(false);
    setBlockCompleted(false);
    setQuestionsAnswered(0);
    setCorrectAnswers(0);
    setQuestReward(null);
    setNextLessonData(null);
    setShowSparkAnimation(false);
    setShowOrbChat(false);
    setOrbQuestion('');
    setOrbConversation([]);
    setShowLimitModal(false);
    
    // Reset session timer
    sessionStartTime.current = Date.now();
    
    if (!selectedChild) {
      navigate('/child-select');
      return;
    }
    
    // Check activity limit before loading the lesson
    checkActivityLimitAndLoad();
  }, [lessonId]);

  const checkActivityLimitAndLoad = async () => {
    try {
      // Check if child can proceed with this activity
      const limitRes = await billingAPI.checkActivityLimit(selectedChild.id);
      const limitInfo = limitRes.data;
      
      setLimitData({
        activitiesCompleted: limitInfo.activities_today || 0,
        dailyLimit: limitInfo.daily_limit || 5
      });
      
      if (!limitInfo.can_proceed) {
        // Show limit modal instead of loading lesson
        setShowLimitModal(true);
        setLoading(false);
        return;
      }
      
      // Limit not reached, proceed to load lesson
      fetchLesson();
    } catch (error) {
      console.error('Error checking activity limit:', error);
      // On error, proceed with lesson (fail open for better UX)
      fetchLesson();
    }
  };

  const fetchLesson = async () => {
    try {
      const res = await lessonsAPI.getById(lessonId);
      setLesson(res.data);
      
      // Start session timer for analytics
      sessionStartTime.current = Date.now();
      
      // Track lesson start for analytics
      if (selectedChild && res.data) {
        analyticsAPI.trackActivity({
          user_id: selectedChild.parent_id,
          child_id: selectedChild.id,
          activity_type: 'lesson_start',
          lesson_id: lessonId,
          realm: res.data.room_id,
          timestamp: new Date().toISOString()
        }).catch(err => console.log('Analytics tracking failed:', err));
      }
    } catch (error) {
      toast.error('Failed to load lesson');
      navigate('/temple');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (lesson && currentBlockIndex < lesson.blocks.length - 1) {
      setCurrentBlockIndex(currentBlockIndex + 1);
      setUserAnswer('');
      setShowFeedback(false);
      setBlockCompleted(false); // Reset block completion for next block
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentBlockIndex > 0) {
      setCurrentBlockIndex(currentBlockIndex - 1);
      setUserAnswer('');
      setShowFeedback(false);
    }
  };

  const handleCheckAnswer = () => {
    const currentBlock = lesson.blocks[currentBlockIndex];
    const correct = userAnswer.trim().toLowerCase() === currentBlock.correct_answer?.trim().toLowerCase();
    setIsCorrect(correct);
    setShowFeedback(true);
    setBlockCompleted(true); // Mark block as completed when answer is checked
    
    // Track question stats for skill analytics
    setQuestionsAnswered(prev => prev + 1);
    if (correct) {
      setCorrectAnswers(prev => prev + 1);
    }
  };

  // Handler for interactive block completion
  const handleBlockComplete = (success, additionalData = null) => {
    setBlockCompleted(true);
    
    // Track as a question for skill analytics (only for quiz-type blocks)
    const currentBlock = lesson?.blocks?.[currentBlockIndex];
    const isStoryBlock = currentBlock?.type === 'story' || currentBlock?.type === 'narration';
    
    if (!isStoryBlock) {
      setQuestionsAnswered(prev => prev + 1);
      if (success) {
        setCorrectAnswers(prev => prev + 1);
      }
    }
    
    // For story/narration blocks, auto-advance to next block
    if (isStoryBlock) {
      handleNext();
    }
  };

  const handleComplete = async () => {
    try {
      // Calculate session duration
      const durationSeconds = sessionStartTime.current 
        ? Math.floor((Date.now() - sessionStartTime.current) / 1000)
        : 0;
      
      // Get the current lesson ID before anything else
      const currentLessonId = lesson?.id || lessonId;
      
      // Complete the lesson first and wait for it
      await completionsAPI.complete({
        lesson_id: currentLessonId,
        child_id: selectedChild.id,
      });
      
      // Record lesson completion with skill tracking
      if (selectedChild && lesson) {
        try {
          await skillProgressAPI.recordLessonCompletion(
            selectedChild.id, 
            currentLessonId, 
            questionsAnswered, 
            correctAnswers
          );
        } catch (skillErr) {
          console.log('Skill tracking failed:', skillErr);
        }
      }
      
      // Track lesson completion for analytics with duration
      if (selectedChild && lesson) {
        analyticsAPI.trackActivity({
          user_id: selectedChild.parent_id,
          child_id: selectedChild.id,
          activity_type: 'lesson_complete',
          lesson_id: currentLessonId,
          realm: lesson.room_id,
          duration_seconds: durationSeconds,
          questions_answered: questionsAnswered,
          correct_answers: correctAnswers,
          timestamp: new Date().toISOString()
        }).catch(err => console.log('Analytics tracking failed:', err));
      }
      
      // If there's an active quest, complete it
      if (activeQuestId && selectedChild) {
        try {
          const questRes = await questsAPI.completeQuest(selectedChild.id, activeQuestId);
          if (questRes.data?.sparks_awarded > 0) {
            setQuestReward({
              amount: questRes.data.sparks_awarded,
              type: questRes.data.reward_type,
              message: questRes.data.message
            });
          }
          // Clear the active quest
          localStorage.removeItem('activeQuestId');
        } catch (questError) {
          console.error('Error completing quest:', questError);
          // Quest might already be completed, that's okay
        }
      }
      
      // Fetch next lesson recommendation - ALWAYS pass the current lesson ID
      if (selectedChild && lesson) {
        try {
          const nextLessonRes = await lessonsAPI.getNextRecommended(
            selectedChild.id, 
            lesson.room_id, 
            currentLessonId  // Always pass the current lesson to exclude it
          );
          setNextLessonData(nextLessonRes.data);
        } catch (err) {
          console.log('Failed to get next lesson:', err);
        }
        
        // Fetch journey progress
        try {
          const journeyRes = await journeyAPI.getCurrentJourney(selectedChild.id);
          setJourneyProgress(journeyRes.data);
        } catch (err) {
          console.log('Failed to get journey progress:', err);
        }
      }
      
      // Trigger spark animation
      setShowSparkAnimation(true);
      setTimeout(() => setShowSparkAnimation(false), 3000);
      
      setCompleted(true);
      toast.success('Lesson completed! 🎉');
    } catch (error) {
      console.error('Error completing lesson:', error);
      setCompleted(true);
    }
  };

  // Ask the Orb AI tutor
  const handleAskOrb = async () => {
    if (!orbQuestion.trim() || !selectedChild) return;
    
    setOrbLoading(true);
    const question = orbQuestion.trim();
    setOrbQuestion('');
    
    // Add user question to conversation
    setOrbConversation(prev => [...prev, { type: 'user', content: question }]);
    
    try {
      const res = await askOrbAPI.ask(question, lessonId, selectedChild.id);
      
      // Add orb response to conversation
      setOrbConversation(prev => [...prev, { 
        type: 'orb', 
        content: res.data?.answer || 'The Orb is thinking...',
        isSafe: res.data?.is_safe
      }]);
    } catch (error) {
      console.error('Error asking orb:', error);
      const errorMessage = error.response?.status === 429 
        ? 'Too many questions! Take a breath and try again in a moment.'
        : 'The Orb\'s magic flickered. Try asking again!';
      
      setOrbConversation(prev => [...prev, { 
        type: 'orb', 
        content: errorMessage,
        isError: true
      }]);
    } finally {
      setOrbLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-white text-xl font-philosopher">Loading lesson...</div>
      </div>
    );
  }

  // Show activity limit modal if limit reached
  if (showLimitModal) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <ActivityLimitModal
          isOpen={true}
          onClose={() => navigate('/temple')}
          childName={selectedChild?.name || 'Explorer'}
          activitiesCompleted={limitData.activitiesCompleted}
          dailyLimit={limitData.dailyLimit}
        />
      </div>
    );
  }

  if (!lesson || !lesson.blocks || lesson.blocks.length === 0) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-400 mb-4">This lesson has no content yet.</p>
          <button
            onClick={() => navigate('/temple')}
            className="text-indigo-400 hover:text-indigo-300"
          >
            Back to Temple
          </button>
        </div>
      </div>
    );
  }

  const currentBlock = lesson.blocks[currentBlockIndex];
  const isLastBlock = currentBlockIndex === lesson.blocks.length - 1;
  const progress = ((currentBlockIndex + 1) / lesson.blocks.length) * 100;

  // Helper to determine if a block type requires completion before proceeding
  const isInteractiveBlock = (type) => {
    const interactiveTypes = [
      'question', 'true_false', 'matching', 'drag_drop', 'sequence', 
      'sorting', 'fill_blank', 'quick_recall', 'logic_puzzle', 'challenge',
      'creative_writing', 'drawing', 'music', 'rhythm', 'reflection'
    ];
    return interactiveTypes.includes(type);
  };

  if (completed) {
    return (
      <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-4 relative overflow-hidden">
        {/* Animated Spark Background */}
        {showSparkAnimation && (
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-float-up"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                  animationDuration: `${2 + Math.random() * 2}s`
                }}
              >
                <Sparkles className={`w-6 h-6 ${['text-yellow-400', 'text-purple-400', 'text-cyan-400', 'text-pink-400'][i % 4]}`} />
              </div>
            ))}
          </div>
        )}
        
        <div className="text-center max-w-lg relative z-10">
          {/* Avatar with Celebration Animation */}
          {selectedChild?.avatar && Object.keys(selectedChild.avatar).length > 0 ? (
            <div className="relative mb-6 mx-auto w-32 h-32">
              <AvatarPreview 
                avatar={selectedChild.avatar} 
                size="xl" 
                type="explorer"
                showCelebration={showSparkAnimation}
                showAura={true}
                auraColor="#FBBF24"
              />
            </div>
          ) : (
            /* Fallback Trophy Icon */
            <div className="relative mb-8">
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/30 via-purple-500/30 to-cyan-500/30 rounded-full blur-2xl animate-pulse" />
              <Trophy className="w-24 h-24 text-yellow-400 mx-auto relative animate-bounce" style={{ animationDuration: '2s' }} />
            </div>
          )}
          
          <h1 className="text-4xl font-philosopher font-bold text-white glow-text mb-4">
            Wonderful Work!
          </h1>
          <p className="text-lg text-slate-300 mb-6">
            You've completed <span className="text-indigo-400 font-semibold">{lesson.title}</span>
          </p>
          
          {/* Progress Stats */}
          {nextLessonData && (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-4 mb-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Star className="w-5 h-5 text-yellow-400" />
                <span className="text-slate-300">Realm Progress</span>
              </div>
              <div className="flex items-center justify-center gap-4">
                <span className="text-2xl font-bold text-white">{nextLessonData.lessons_completed}</span>
                <span className="text-slate-500">/</span>
                <span className="text-2xl font-bold text-slate-400">{nextLessonData.total_lessons}</span>
              </div>
              <div className="mt-3 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-1000"
                  style={{ width: `${nextLessonData.progress_percent}%` }}
                />
              </div>
              <p className="text-sm text-slate-400 mt-2">
                {nextLessonData.progress_percent.toFixed(0)}% complete in this realm
              </p>
            </div>
          )}
          
          {/* Quest Reward Animation */}
          {questReward && (
            <div className="mb-6 animate-bounce" style={{ animationDuration: '1.5s' }}>
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-500/30 to-orange-500/30 border border-yellow-500/50 rounded-2xl px-6 py-4">
                <Sparkles className="w-8 h-8 text-yellow-400" />
                <div className="text-left">
                  <p className="text-yellow-400 font-bold text-lg">Quest Complete!</p>
                  <p className="text-white">+{questReward.amount} {questReward.type} sparks</p>
                </div>
              </div>
            </div>
          )}
          
          {/* Journey Progress Indicator */}
          {journeyProgress && (
            <div className="mb-6 bg-gradient-to-r from-slate-800/80 to-slate-900/80 border border-slate-700/50 rounded-2xl p-4" data-testid="journey-progress">
              <div className="flex items-center gap-3 mb-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${journeyProgress.journey_color}20`, borderColor: `${journeyProgress.journey_color}50`, borderWidth: '1px' }}
                >
                  <Compass className="w-5 h-5" style={{ color: journeyProgress.journey_color }} />
                </div>
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-wider" style={{ color: journeyProgress.journey_color }}>Your Journey</p>
                  <p className="text-white font-semibold text-sm">{journeyProgress.journey_name}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-white">
                    {journeyProgress.completed_lessons}/{journeyProgress.total_lessons}
                  </p>
                </div>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className="h-full transition-all duration-1000 rounded-full"
                  style={{ 
                    width: `${journeyProgress.progress_percent}%`,
                    backgroundColor: journeyProgress.journey_color 
                  }}
                />
              </div>
              <p className="text-xs text-slate-400 mt-2 text-center">
                {journeyProgress.progress_percent.toFixed(0)}% complete
                {journeyProgress.is_completed && ' - Journey Complete! 🎉'}
              </p>
            </div>
          )}
          
          {/* Continue Your Journey Button - shows if journey has more lessons */}
          {journeyProgress?.next_lesson_id && (
            <div className="mb-6">
              <p className="text-sm text-slate-400 mb-3">Continue your adventure</p>
              <button
                data-testid="continue-journey-btn"
                onClick={() => navigate(`/lesson-player/${journeyProgress.next_lesson_id}`)}
                className="w-full bg-gradient-to-r hover:scale-[1.02] border rounded-2xl p-4 text-left transition-all duration-300 group cursor-pointer"
                style={{ 
                  background: `linear-gradient(135deg, ${journeyProgress.journey_color}15, ${journeyProgress.journey_color}08)`,
                  borderColor: `${journeyProgress.journey_color}40`
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Map className="w-5 h-5" style={{ color: journeyProgress.journey_color }} />
                    <div>
                      <p className="text-xs uppercase tracking-wider mb-1" style={{ color: journeyProgress.journey_color }}>Continue {journeyProgress.journey_name}</p>
                      <p className="text-white font-semibold">{journeyProgress.next_lesson_title || 'Next Lesson'}</p>
                    </div>
                  </div>
                  <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" style={{ color: journeyProgress.journey_color }} />
                </div>
              </button>
            </div>
          )}
          
          {/* Next Lesson Recommendation (fallback if no journey next) */}
          {nextLessonData?.next_lesson && !journeyProgress?.next_lesson_id && (
            <div className="mb-6">
              <p className="text-sm text-slate-400 mb-3">Ready for more?</p>
              <button
                data-testid="next-lesson-btn"
                onClick={() => navigate(`/lesson-player/${nextLessonData.next_lesson.id}`)}
                className="w-full bg-gradient-to-r from-indigo-500/20 to-purple-500/20 hover:from-indigo-500/30 hover:to-purple-500/30 border border-indigo-500/30 rounded-2xl p-4 text-left transition-all duration-300 hover:scale-[1.02] group cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-indigo-400 uppercase tracking-wider mb-1">Next Lesson</p>
                    <p className="text-white font-semibold">{nextLessonData.next_lesson.title}</p>
                    {nextLessonData.next_lesson.description && (
                      <p className="text-sm text-slate-400 mt-1 line-clamp-1">{nextLessonData.next_lesson.description}</p>
                    )}
                  </div>
                  <ChevronRight className="w-6 h-6 text-indigo-400 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            </div>
          )}
          
          {/* All Complete Message */}
          {nextLessonData?.all_complete && (
            <div className="mb-6 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-2xl p-4">
              <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <p className="text-green-400 font-semibold text-center">Realm Complete!</p>
              <p className="text-sm text-slate-300 mt-1 text-center">You've finished all lessons in this realm!</p>
            </div>
          )}
          
          {/* Fallback when no next lesson and not all complete */}
          {nextLessonData && !nextLessonData.next_lesson && !nextLessonData.all_complete && (
            <div className="mb-6 bg-gradient-to-r from-amber-500/20 to-orange-500/20 border border-amber-500/30 rounded-2xl p-4 text-center">
              <Sparkles className="w-8 h-8 text-amber-400 mx-auto mb-2" />
              <p className="text-amber-300 font-semibold">Great progress!</p>
              <p className="text-sm text-slate-300 mt-1">Return to the Temple to explore more realms.</p>
            </div>
          )}
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              data-testid="back-to-temple-btn"
              onClick={() => navigate('/temple')}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-8 py-3 transition-all duration-300 hover:scale-105 backdrop-blur-md shadow-[0_0_15px_rgba(255,255,255,0.1)]"
            >
              Back to Temple
            </button>
            
            {nextLessonData?.all_complete && (
              <button
                onClick={() => navigate('/temple-sky')}
                className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-full px-8 py-3 transition-all duration-300 hover:opacity-90 hover:scale-105"
              >
                View My Sky
              </button>
            )}
          </div>
        </div>
        
        {/* Floating animation keyframes */}
        <style>{`
          @keyframes float-up {
            0% {
              transform: translateY(100vh) rotate(0deg);
              opacity: 1;
            }
            100% {
              transform: translateY(-100px) rotate(360deg);
              opacity: 0;
            }
          }
          .animate-float-up {
            animation: float-up 3s ease-out forwards;
          }
        `}</style>
      </div>
    );
  }

  return (
    <LearningIntelligenceProvider lessonId={lessonId}>
    <div className="min-h-screen bg-[#0F172A] px-6 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            data-testid="exit-lesson-btn"
            onClick={() => navigate('/temple')}
            className="text-slate-400 hover:text-white transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Exit Lesson</span>
          </button>
          <div className="text-slate-400 text-sm">
            {currentBlockIndex + 1} / {lesson.blocks.length}
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Lesson Title */}
        <h1 className="text-3xl font-philosopher font-bold text-white glow-text mb-4 text-center">
          {lesson.title}
        </h1>

        {/* Phase Indicator - shows current learning phase */}
        <LessonPhaseIndicator 
          blockType={currentBlock.type}
          blockIndex={currentBlockIndex}
          totalBlocks={lesson.blocks.length}
          explicitPhase={currentBlock.phase}
        />

        {/* Block Content */}
        <div className="glass-panel p-8 rounded-2xl mb-6 min-h-[300px]">
          {/* Basic Content Types */}
          {currentBlock.type === 'text' && (
            <div className="prose prose-invert max-w-none">
              {/* Aret'Hera Voice for ALL text content - accessibility feature */}
              {currentBlock.content && (
                <div className="mb-4">
                  <AretHeraVoice 
                    text={currentBlock.content}
                    blockId={currentBlock.id}
                  />
                </div>
              )}
              {currentBlock.title && (
                <h3 className="text-2xl font-bold text-white mb-4">{currentBlock.title}</h3>
              )}
              <p className="text-lg text-slate-200 leading-relaxed whitespace-pre-wrap">
                {currentBlock.content}
              </p>
              {currentBlock.highlight && (
                <div className="mt-4 p-4 bg-amber-500/20 border border-amber-500/40 rounded-xl">
                  <p className="text-amber-200 font-medium">{currentBlock.highlight}</p>
                </div>
              )}
            </div>
          )}

          {currentBlock.type === 'image' && (
            <div className="text-center">
              <img
                src={currentBlock.content}
                alt="Lesson content"
                className="max-w-full h-auto rounded-xl mx-auto"
                style={{ maxHeight: '400px' }}
              />
            </div>
          )}

          {currentBlock.type === 'video' && (
            <div className="aspect-video">
              <iframe
                src={currentBlock.content}
                className="w-full h-full rounded-xl"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}

          {currentBlock.type === 'link' && (
            <div className="text-center">
              <p className="text-slate-300 mb-4">Click the link below to continue:</p>
              <a
                href={currentBlock.content}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-6 py-3 transition-all duration-300 hover:scale-105"
              >
                <span>Open Link</span>
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          )}

          {/* Story / Narration Block - with Aret'Hera Voice */}
          {(currentBlock.type === 'story' || currentBlock.type === 'narration') && (
            <div>
              {/* Voice playback for ALL story/narration content - accessibility feature */}
              {currentBlock.content && (
                <div className="mb-4">
                  <AretHeraVoice 
                    text={currentBlock.content}
                    blockId={currentBlock.id}
                  />
                </div>
              )}
              <StoryNarration block={currentBlock} onComplete={handleBlockComplete} />
            </div>
          )}

          {/* Multiple Choice Question */}
          {currentBlock.type === 'question' && (
            <QuestionBlockWithIntelligence 
              block={currentBlock}
              userAnswer={userAnswer}
              setUserAnswer={setUserAnswer}
              showFeedback={showFeedback}
              isCorrect={isCorrect}
              handleCheckAnswer={handleCheckAnswer}
              setShowFeedback={setShowFeedback}
              setIsCorrect={setIsCorrect}
              setBlockCompleted={setBlockCompleted}
              setQuestionsAnswered={setQuestionsAnswered}
              setCorrectAnswers={setCorrectAnswers}
              lesson={lesson}
            />
          )}

          {/* True/False Block */}
          {currentBlock.type === 'true_false' && (
            <TrueFalse block={currentBlock} onComplete={handleBlockComplete} />
          )}

          {/* Matching Pairs Block */}
          {currentBlock.type === 'matching' && (
            <MatchingPairs block={currentBlock} onComplete={handleBlockComplete} />
          )}

          {/* Drag & Drop Ordering Block */}
          {(currentBlock.type === 'drag_drop' || currentBlock.type === 'sequence') && (
            <DragDropOrder block={currentBlock} onComplete={handleBlockComplete} />
          )}

          {/* Sorting / Categorizing Block */}
          {currentBlock.type === 'sorting' && (
            <SortingActivity block={currentBlock} onComplete={handleBlockComplete} />
          )}

          {/* Fill in the Blank Block */}
          {currentBlock.type === 'fill_blank' && (
            <FillInBlank block={currentBlock} onComplete={handleBlockComplete} />
          )}

          {/* Quick Recall Block */}
          {currentBlock.type === 'quick_recall' && (
            <QuickRecall block={currentBlock} onComplete={handleBlockComplete} />
          )}

          {/* Logic Puzzle / Challenge Block */}
          {(currentBlock.type === 'logic_puzzle' || currentBlock.type === 'challenge') && (
            <LogicPuzzle block={currentBlock} onComplete={handleBlockComplete} />
          )}

          {/* Creative Writing Block */}
          {currentBlock.type === 'creative_writing' && (
            <CreativeWriting block={currentBlock} onComplete={handleBlockComplete} />
          )}

          {/* Drawing Activity Block */}
          {currentBlock.type === 'drawing' && (
            <DrawingActivity block={currentBlock} onComplete={handleBlockComplete} />
          )}

          {/* Music / Rhythm Block */}
          {(currentBlock.type === 'music' || currentBlock.type === 'rhythm') && (
            <MusicRhythm block={currentBlock} onComplete={handleBlockComplete} />
          )}

          {/* Reflection Block */}
          {currentBlock.type === 'reflection' && (
            <ReflectionActivity block={currentBlock} onComplete={handleBlockComplete} />
          )}

          {/* Visual Teaching Block */}
          {currentBlock.type === 'visual' && (
            <div className="space-y-6">
              {currentBlock.title && (
                <h3 className="text-2xl font-bold text-white text-center">{currentBlock.title}</h3>
              )}
              {currentBlock.content && (
                <p className="text-slate-300 text-center">{currentBlock.content}</p>
              )}
              {currentBlock.visual_data && (
                <div className="bg-slate-800/50 rounded-2xl p-6 border border-white/10">
                  {currentBlock.visual_data.phases && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {currentBlock.visual_data.phases.map((phase, idx) => (
                        <div key={idx} className="text-center p-4 bg-slate-900/50 rounded-xl">
                          <div className="text-4xl mb-2">{phase.emoji}</div>
                          <p className="text-white font-semibold text-sm">{phase.name}</p>
                          <p className="text-slate-400 text-xs mt-1">{phase.visibility}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {currentBlock.visual_data.types && (
                    <div className="space-y-3">
                      {currentBlock.visual_data.types.map((type, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-3 bg-slate-900/50 rounded-xl">
                          <span className="text-2xl">{type.example}</span>
                          <div>
                            <p className="text-white font-semibold">{type.name}</p>
                            <p className="text-slate-400 text-sm">Core: {type.core}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Worked Example Block */}
          {currentBlock.type === 'worked_example' && (
            <div className="space-y-6">
              {currentBlock.title && (
                <h3 className="text-xl font-bold text-white">{currentBlock.title}</h3>
              )}
              
              {/* Aret'Hera Voice for worked examples - reads setup + steps */}
              {currentBlock.setup && (
                <div className="mb-2">
                  <AretHeraVoice 
                    text={`${currentBlock.setup}. ${currentBlock.steps ? currentBlock.steps.join('. ') : ''}`}
                    blockId={currentBlock.id}
                  />
                </div>
              )}
              
              {/* Setup/Problem */}
              <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl p-5 border border-blue-500/30">
                <p className="text-slate-400 text-sm uppercase tracking-wider mb-2">Problem</p>
                <p className="text-white text-lg">{currentBlock.setup}</p>
              </div>
              
              {/* Steps */}
              {currentBlock.steps && (
                <div className="space-y-3">
                  <p className="text-slate-400 text-sm uppercase tracking-wider">Let's solve it step by step:</p>
                  {currentBlock.steps.map((step, idx) => (
                    <div 
                      key={idx}
                      className="flex items-start gap-4 bg-slate-800/50 rounded-xl p-4 border border-white/5"
                    >
                      <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                        <span className="text-cyan-400 font-bold">{idx + 1}</span>
                      </div>
                      <p className="text-slate-200">{step}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Conclusion */}
              {currentBlock.conclusion && (
                <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl p-5 border border-green-500/30 text-center">
                  <p className="text-green-400 text-sm uppercase tracking-wider mb-2">Answer</p>
                  <p className="text-white text-xl font-semibold">{currentBlock.conclusion}</p>
                </div>
              )}
              
              {/* Visual Hint */}
              {currentBlock.visual_hint && (
                <div className="text-center">
                  <p className="text-amber-400 text-sm italic">💡 {currentBlock.visual_hint}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex justify-between gap-4">
          <button
            data-testid="previous-block-btn"
            onClick={handlePrevious}
            disabled={currentBlockIndex === 0}
            className="flex items-center gap-2 bg-slate-800/50 hover:bg-slate-700/50 text-white rounded-full px-6 py-3 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Previous</span>
          </button>

          <button
            data-testid="next-block-btn"
            onClick={handleNext}
            disabled={isInteractiveBlock(currentBlock.type) && !blockCompleted && currentBlock.type !== 'story' && currentBlock.type !== 'narration'}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-6 py-3 transition-all duration-300 hover:scale-105 backdrop-blur-md shadow-[0_0_15px_rgba(255,255,255,0.1)] disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <span>{isLastBlock ? 'Complete' : 'Next'}</span>
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>

        {/* Ask the Orb Button */}
        <button
          data-testid="ask-orb-btn"
          onClick={() => setShowOrbChat(true)}
          className="fixed bottom-20 sm:bottom-6 right-6 w-14 h-14 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg hover:scale-110 transition-transform flex items-center justify-center"
        >
          <MessageCircle className="w-6 h-6" />
        </button>

        {/* Ask the Orb Chat Modal */}
        {showOrbChat && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4">
            <div className="glass-panel rounded-t-3xl sm:rounded-3xl w-full max-w-md max-h-[80vh] flex flex-col">
              {/* Header */}
              <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Ask the Orb</h3>
                    <p className="text-xs text-slate-400">AI Learning Guide</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowOrbChat(false)}
                  className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  data-testid="close-orb-chat"
                >
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {/* Conversation */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {orbConversation.length === 0 && (
                  <div className="text-center py-8">
                    <Sparkles className="w-12 h-12 text-cyan-400 mx-auto mb-4 opacity-50" />
                    <p className="text-slate-400 text-sm">
                      Ask me anything about "{lesson?.title}"!
                    </p>
                    <p className="text-slate-500 text-xs mt-2">
                      I'll help explain things in a fun, simple way.
                    </p>
                  </div>
                )}
                
                {orbConversation.map((msg, i) => (
                  <div
                    key={i}
                    className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 rounded-2xl ${
                        msg.type === 'user'
                          ? 'bg-indigo-500/30 text-white'
                          : msg.isError
                          ? 'bg-red-500/20 text-red-300'
                          : 'bg-cyan-500/20 text-cyan-100'
                      }`}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    </div>
                  </div>
                ))}
                
                {orbLoading && (
                  <div className="flex justify-start">
                    <div className="bg-cyan-500/20 p-3 rounded-2xl">
                      <Loader2 className="w-5 h-5 text-cyan-400 animate-spin" />
                    </div>
                  </div>
                )}
              </div>

              {/* Input */}
              <div className="p-4 border-t border-white/10">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={orbQuestion}
                    onChange={(e) => setOrbQuestion(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAskOrb()}
                    placeholder="Ask your question..."
                    className="flex-1 bg-slate-800/50 border border-white/10 rounded-full px-4 py-2 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/50"
                    data-testid="orb-question-input"
                  />
                  <button
                    onClick={handleAskOrb}
                    disabled={!orbQuestion.trim() || orbLoading}
                    className="w-10 h-10 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center text-white disabled:opacity-50 hover:scale-105 transition-transform"
                    data-testid="send-orb-question"
                  >
                    <Send className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Orb only answers questions about the lesson
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </LearningIntelligenceProvider>
  );
};

/**
 * Question Block with Learning Intelligence
 * Integrates adaptive learning features: hints on hesitation, extra examples on struggles
 */
const QuestionBlockWithIntelligence = ({ 
  block, 
  userAnswer, 
  setUserAnswer, 
  showFeedback, 
  isCorrect,
  handleCheckAnswer: parentCheckAnswer,
  setShowFeedback,
  setIsCorrect,
  setBlockCompleted,
  setQuestionsAnswered,
  setCorrectAnswers,
  lesson
}) => {
  const {
    showHintLayer,
    currentHint,
    showExtraExample,
    extraExampleTopic,
    fastTrackMode,
    onQuestionStart,
    onQuestionAnswer,
    dismissExtraExample,
    dismissHint,
    canSkipBlock,
    skipBlock
  } = useLearningIntelligence();
  
  const [localHint, setLocalHint] = React.useState(null);
  const [showLocalHint, setShowLocalHint] = React.useState(false);
  const questionStartRef = React.useRef(null);
  const hintTimerRef = React.useRef(null);
  
  // Start tracking when block mounts
  React.useEffect(() => {
    questionStartRef.current = Date.now();
    onQuestionStart(block);
    
    // Set up hesitation detection (15 seconds)
    if (block.hint || block.show_hints) {
      hintTimerRef.current = setTimeout(() => {
        setLocalHint(block.hint || "Take your time and think about what you learned in the examples!");
        setShowLocalHint(true);
      }, 15000);
    }
    
    return () => {
      if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    };
  }, [block.id]);
  
  // Find a relevant worked example for extra help
  const findWorkedExample = () => {
    if (!lesson?.blocks) return null;
    const examples = lesson.blocks.filter(b => b.type === 'worked_example');
    return examples.length > 0 ? examples[examples.length - 1] : null;
  };
  
  // Enhanced check answer with intelligence tracking
  const handleIntelligentCheckAnswer = () => {
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    
    const correct = userAnswer.trim().toLowerCase() === block.correct_answer?.trim().toLowerCase();
    setIsCorrect(correct);
    setShowFeedback(true);
    setBlockCompleted(true);
    
    // Track for skill analytics
    setQuestionsAnswered(prev => prev + 1);
    if (correct) {
      setCorrectAnswers(prev => prev + 1);
    }
    
    // Report to intelligence layer
    onQuestionAnswer(correct, { topic: block.content?.slice(0, 50) });
  };
  
  // Handle fast-track skip
  const handleSkip = () => {
    skipBlock();
    setBlockCompleted(true);
  };
  
  return (
    <div className="space-y-6">
      {/* Fast Track Indicator */}
      {fastTrackMode && canSkipBlock('question') && !showFeedback && (
        <FastTrackIndicator onSkip={handleSkip} blockType="question" />
      )}
      
      {/* Extra Worked Example Modal */}
      {showExtraExample && (
        <ExtraExampleModal 
          topic={extraExampleTopic}
          onDismiss={dismissExtraExample}
          workedExample={findWorkedExample()}
        />
      )}
      
      {/* Voice for ALL question content */}
      {block.content && (
        <div className="mb-2">
          <AretHeraVoice 
            text={block.content}
            blockId={block.id}
          />
        </div>
      )}
      
      <p className="text-xl text-white font-semibold mb-4">{block.content}</p>
      
      {/* Hint Layer - shows on hesitation */}
      {(showLocalHint || showHintLayer) && !showFeedback && (
        <HintLayer 
          hint={localHint || currentHint || block.hint} 
          onDismiss={() => {
            setShowLocalHint(false);
            dismissHint();
          }} 
        />
      )}

      {block.options && block.options.length > 0 ? (
        <div className="space-y-3">
          {block.options.map((option, idx) => (
            <button
              key={idx}
              data-testid={`question-option-${idx}`}
              onClick={() => setUserAnswer(option)}
              className={`w-full text-left p-4 rounded-xl transition-all ${
                userAnswer === option
                  ? 'bg-indigo-500/30 border-2 border-indigo-400'
                  : 'bg-slate-800/50 border-2 border-transparent hover:bg-slate-700/50'
              }`}
            >
              <span className="text-white">{option}</span>
            </button>
          ))}
        </div>
      ) : (
        <input
          data-testid="question-answer-input"
          type="text"
          value={userAnswer}
          onChange={(e) => setUserAnswer(e.target.value)}
          className="w-full bg-slate-950/50 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all outline-none"
          placeholder="Type your answer..."
        />
      )}

      {!showFeedback && (
        <button
          data-testid="check-answer-btn"
          onClick={handleIntelligentCheckAnswer}
          disabled={!userAnswer.trim()}
          className="w-full bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full px-6 py-3 transition-all duration-300 hover:scale-105 backdrop-blur-md shadow-[0_0_15px_rgba(255,255,255,0.1)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Check Answer
        </button>
      )}

      {showFeedback && (
        <div
          className={`p-4 rounded-xl ${
            isCorrect ? 'bg-green-500/20 border border-green-500/50' : 'bg-red-500/20 border border-red-500/50'
          }`}
        >
          <p className={`font-semibold ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
            {isCorrect ? '✓ Correct!' : '✗ Not quite right'}
          </p>
          {!isCorrect && block.correct_answer && (
            <p className="text-slate-300 mt-2">
              The correct answer is: <span className="font-semibold">{block.correct_answer}</span>
            </p>
          )}
          {!isCorrect && block.explanation && (
            <p className="text-slate-400 mt-2 text-sm">{block.explanation}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default LessonPlayerPage;
